import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import {
	postInventaireArticle,
	postInventaireArticleReset,
	deleteInventaireArticle,
	deleteInventaireArticleReset,
} from "../../../store/actions/atelierInventaireActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"

const toNum = (v) => {
	const n = parseFloat(v)
	return Number.isNaN(n) ? 0 : n
}

// Même langage visuel que le modal commande : HT neutre, TTC accentué.
const htBtn = (ht) => ({
	height: 38,
	minWidth: 48,
	borderRadius: 10,
	border: `1px solid ${ht ? COLORS.border : COLORS.accent}`,
	backgroundColor: ht ? COLORS.surface : COLORS.accentLight,
	color: ht ? COLORS.textMuted : COLORS.accent,
	fontSize: 12,
	fontWeight: 600,
	cursor: "pointer",
	fontFamily: "inherit",
})

// Édition d'un article d'inventaire (modal V2). POST/DELETE renvoient les
// articles de la catégorie → le reducer met la table à jour seul. La
// suppression est protégée si l'article est utilisé (commandes / modèles).
const ArticleModalV2 = ({ article, categorieId, onClose }) => {
	const dispatch = useDispatch()
	const postState = useSelector((s) => s.atelierInventaire.postArticle)
	const deleteState = useSelector((s) => s.atelierInventaire.deleteArticle)
	const isBoss = useSelector((s) =>
		s.navInfo.login.groups.includes("boss"),
	)
	const isCreate = !article.id
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [form, setForm] = useState({
		nom: article.nom || "",
		quantite: article.quantite != null ? String(article.quantite) : "0",
		prixVente: article.prixVente != null ? String(article.prixVente) : "0",
		isPrixVenteHT: !!article.isPrixVenteHT,
		limite: article.limite != null ? String(article.limite) : "0",
		commission:
			article.commission != null ? String(article.commission) : "0",
	})
	const update = (k, v) => setForm((p) => ({ ...p, [k]: v }))

	useEffect(() => {
		if (!postState.saved && !deleteState.deleted) return
		const t = setTimeout(() => {
			dispatch(postInventaireArticleReset())
			dispatch(deleteInventaireArticleReset())
			onClose()
		}, 800)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.saved, deleteState.deleted])

	useEffect(
		() => () => {
			dispatch(postInventaireArticleReset())
			dispatch(deleteInventaireArticleReset())
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	useEffect(() => {
		if (deleteState.error) setConfirmDelete(false)
	}, [deleteState.error])

	const invalid = !form.nom.trim()

	const save = () => {
		const payload = {
			id: article.id || null,
			categorie: article.categorie || categorieId,
			nom: form.nom.trim(),
			quantite: toNum(form.quantite),
			prixVente: toNum(form.prixVente),
			isPrixVenteHT: form.isPrixVenteHT,
			limite: toNum(form.limite),
			commission: toNum(form.commission),
		}
		dispatch(postInventaireArticle(JSON.stringify(payload)))
	}
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		dispatch(deleteInventaireArticle(JSON.stringify({ id: article.id })))
	}

	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted

	const buttons = []
	if (!isCreate) {
		buttons.push({
			title: deleteState.deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ?"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: postState.saving ? (
			<FontAwesomeIcon icon={faSync} spin />
		) : postState.saved ? (
			<FontAwesomeIcon icon={faCheck} />
		) : (
			"Enregistrer"
		),
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid,
	})

	// Le stock (quantite) ne se modifie PAS ici (géré via Stock/Commandes) ; on
	// conserve la valeur existante dans le payload sans l'exposer.
	const numFields = [{ key: "limite", label: "Limite (seuil)" }]
	if (isBoss) numFields.push({ key: "commission", label: "Commission" })

	return (
		<CustomModal
			title={isCreate ? "Nouvel article" : "Éditer l'article"}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
		>
			<div style={gs.fieldGroup}>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Nom<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<input
						style={gs.input}
						value={form.nom}
						onChange={(e) => update("nom", e.target.value)}
						autoFocus
					/>
				</div>
				{/* Prix de vente + interprétation HT/TTC. La bascule ne convertit
				    pas le montant : elle déclare ce que le prix stocké
				    représente (repris par les lignes de fiche via isHT). */}
				<div style={gs.fieldRow}>
					<label style={gs.label}>Prix de vente</label>
					<div style={{ display: "flex", gap: 8 }}>
						<input
							style={{ ...gs.input, flex: 1, minWidth: 0 }}
							type="number"
							step="any"
							value={form.prixVente}
							onChange={(e) =>
								update("prixVente", e.target.value)
							}
						/>
						<button
							type="button"
							style={htBtn(form.isPrixVenteHT)}
							onClick={() =>
								update("isPrixVenteHT", !form.isPrixVenteHT)
							}
							title="Le prix de vente saisi est-il HT ou TTC ?"
						>
							{form.isPrixVenteHT ? "HT" : "TTC"}
						</button>
					</div>
				</div>
				{numFields.map((f) => (
					<div key={f.key} style={gs.fieldRow}>
						<label style={gs.label}>{f.label}</label>
						<input
							style={gs.input}
							type="number"
							step="any"
							value={form[f.key]}
							onChange={(e) => update(f.key, e.target.value)}
						/>
					</div>
				))}
			</div>
		</CustomModal>
	)
}

export default ArticleModalV2
